import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Store, Bike, MapPin, CreditCard, Clock, Search, BadgePercent, ArrowRight, Utensils, CheckCircle2, Globe } from "lucide-react";
import { MarketingShell } from "@/components/marketing/MarketingShell";
import { Seo } from "@/components/marketing/Seo";
import { SITE, faqSchema, breadcrumbSchema } from "@/lib/seo";

const HERO_IMG = "https://images.unsplash.com/photo-1712757248842-04f6e3844374?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100&ixlib=rb-4.1.0";
const BIRYANI_IMG = "https://images.unsplash.com/photo-1782541370275-6761522e39d1?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&ixlib=rb-4.1.0";
const RIDER_IMG = "https://images.unsplash.com/photo-1617347454431-f49d7ff5c3b1?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&ixlib=rb-4.1.0";

const STEPS = [
  { icon: Search, title: "Discover restaurants", text: "Browse local restaurants and menus near you and find exactly what you're craving." },
  { icon: ShoppingBag, title: "Order food online", text: "Add dishes to your cart, apply offers and check out with secure digital payments or cash on delivery." },
  { icon: MapPin, title: "Track till your door", text: "Follow your order live on the map and get it delivered fast by a BiteGoFood delivery partner." },
];
const FEATURES = [
  { icon: Utensils, title: "Order food online", text: "Thousands of dishes from local restaurants, delivered to your door." },
  { icon: Search, title: "Restaurant discovery", text: "Find top-rated restaurants and cuisines near you." },
  { icon: Clock, title: "Live order tracking", text: "Real-time map tracking from kitchen to your doorstep." },
  { icon: CreditCard, title: "Secure payments", text: "Pay online safely or choose cash on delivery." },
  { icon: BadgePercent, title: "Offers & rewards", text: "Save with everyday deals and earn rewards as you order." },
  { icon: CheckCircle2, title: "Reliable support", text: "Friendly customer support whenever you need help." },
];
const FAQS = [
  { q: "What is BiteGoFood?", a: "BiteGoFood is a food delivery platform by Vijay Software Solutions Pvt Ltd that lets you order food online from local restaurants and get it delivered fast. Our tagline is “Food Near You, Faster Than Ever.”" },
  { q: "Where does BiteGoFood deliver?", a: "BiteGoFood is expanding across India. Delivery availability depends on the active service areas shown on the platform. More cities and locations will be added as we expand." },
  { q: "How do I order food on BiteGoFood?", a: "Sign up, choose a restaurant near you, add dishes to your cart and check out with online payment or cash on delivery. You can then track your order live until it reaches you." },
  { q: "How can my restaurant join BiteGoFood?", a: "Restaurants can partner with BiteGoFood through the “Add your restaurant” registration to reach more customers and manage orders from a dedicated dashboard." },
  { q: "How do I become a BiteGoFood delivery partner?", a: "Sign up as a delivery partner through the rider registration to start accepting delivery requests and earning in your city." },
];

const Section = ({ children, className = "" }) => (
  <section className={`max-w-6xl mx-auto px-4 ${className}`}>{children}</section>
);

export default function Landing() {
  return (
    <MarketingShell>
      <Seo
        title="BiteGoFood – Order Food Online, Food Delivery Near You"
        description={SITE.description}
        path="/"
        schemas={[faqSchema(FAQS), breadcrumbSchema([{ name: "Home", path: "/" }])]}
      />

      {/* Hero */}
      <Section className="pt-12 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 bg-coral-light text-coral text-xs font-bold px-3 py-1.5 rounded-full">
            <Globe size={13} /> A modern food-delivery platform built to scale across markets
          </span>
          <h1 className="font-display font-extrabold text-ink mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            BiteGoFood
          </h1>
          <p className="font-display font-bold text-coral text-xl sm:text-2xl mt-2">Food Near You, Faster Than Ever.</p>
          <p className="text-ink-600 text-base sm:text-lg mt-4 max-w-xl">
            Discover great food from local restaurants and get it delivered to you. BiteGoFood connects
            customers, restaurants and delivery partners through one simple platform.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/signup" className="inline-flex items-center gap-2 bg-coral text-white font-bold px-6 py-3.5 rounded-full hover:bg-coral-hover transition-colors" data-testid="hero-order-cta">
              <ShoppingBag size={18} /> Order food now
            </Link>
            <Link to="/login" className="inline-flex items-center gap-2 bg-white text-ink font-bold px-6 py-3.5 rounded-full border border-border hover:border-coral hover:text-coral transition-colors" data-testid="hero-login-cta">
              Log in <ArrowRight size={16} />
            </Link>
          </div>
          <div className="flex flex-wrap gap-5 mt-7 text-sm text-ink-600">
            <span className="flex items-center gap-1.5"><Clock size={15} className="text-coral" /> Live tracking</span>
            <span className="flex items-center gap-1.5"><CreditCard size={15} className="text-coral" /> Secure payments</span>
            <span className="flex items-center gap-1.5"><BadgePercent size={15} className="text-coral" /> Everyday offers</span>
          </div>
        </div>
        <div className="relative">
          <img src={HERO_IMG} alt="Freshly prepared Indian food ready for delivery with BiteGoFood" className="rounded-3xl w-full object-cover aspect-[4/3] shadow-xl" loading="eager" width="1100" height="825" />
          <div className="absolute -bottom-5 -left-3 sm:left-4 bg-white rounded-2xl shadow-lg p-3 flex items-center gap-2.5 border border-border">
            <div className="w-10 h-10 rounded-xl bg-veg/10 flex items-center justify-center"><Bike size={20} className="text-veg" /></div>
            <div><div className="text-xs text-ink-600">Out for delivery</div><div className="font-bold text-sm text-ink">Arriving soon</div></div>
          </div>
        </div>
      </Section>

      {/* How it works */}
      <div className="bg-surface py-16">
        <Section>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl text-center">How BiteGoFood works</h2>
          <p className="text-ink-600 text-center mt-2 max-w-xl mx-auto">Ordering food online with BiteGoFood takes just a few taps.</p>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {STEPS.map((s, i) => (
              <div key={s.title} className="bg-white rounded-2xl border border-border p-6" data-testid={`step-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-coral-light flex items-center justify-center"><s.icon size={22} className="text-coral" /></div>
                <div className="text-xs font-bold text-coral mt-4">STEP {i + 1}</div>
                <h3 className="font-bold text-ink text-lg mt-1">{s.title}</h3>
                <p className="text-ink-600 text-sm mt-1.5">{s.text}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Features */}
      <Section className="py-16">
        <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl text-center">Why order with BiteGoFood</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border p-6 hover:shadow-md transition-shadow">
              <f.icon size={24} className="text-coral" />
              <h3 className="font-bold text-ink text-lg mt-3">{f.title}</h3>
              <p className="text-ink-600 text-sm mt-1.5">{f.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Partners */}
      <div className="bg-surface py-16">
        <Section className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl border border-border overflow-hidden flex flex-col">
            <img src={BIRYANI_IMG} alt="Restaurant partner meal served through BiteGoFood" className="w-full h-48 object-cover" loading="lazy" />
            <div className="p-6 flex-1 flex flex-col">
              <div className="flex items-center gap-2"><Store size={20} className="text-coral" /><h3 className="font-display font-bold text-ink text-xl">For restaurants</h3></div>
              <p className="text-ink-600 text-sm mt-2 flex-1">Partner with BiteGoFood to reach more customers, grow online orders and manage everything from a dedicated restaurant dashboard.</p>
              <Link to="/partner/register" className="inline-flex items-center gap-2 text-coral font-bold text-sm mt-4" data-testid="partner-cta">Add your restaurant <ArrowRight size={15} /></Link>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-border overflow-hidden flex flex-col">
            <img src={RIDER_IMG} alt="BiteGoFood delivery partner riding to deliver an order" className="w-full h-48 object-cover" loading="lazy" />
            <div className="p-6 flex-1 flex flex-col">
              <div className="flex items-center gap-2"><Bike size={20} className="text-coral" /><h3 className="font-display font-bold text-ink text-xl">For delivery partners</h3></div>
              <p className="text-ink-600 text-sm mt-2 flex-1">Become a BiteGoFood delivery partner to accept delivery requests, deliver orders in your city and earn on a flexible schedule.</p>
              <Link to="/rider/register" className="inline-flex items-center gap-2 text-coral font-bold text-sm mt-4" data-testid="rider-cta">Become a delivery partner <ArrowRight size={15} /></Link>
            </div>
          </div>
        </Section>
      </div>

      {/* Locations — India-level positioning */}
      <Section className="py-16 text-center">
        <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">Built for local communities, designed to scale</h2>
        <p className="text-ink-600 mt-2 max-w-xl mx-auto">BiteGoFood is built to connect customers, restaurants and delivery partners across India as we expand into new cities and service areas.</p>
        <div className="flex flex-wrap justify-center gap-3 mt-6" data-testid="locations-chips">
          <span className="inline-flex items-center gap-2 bg-coral-light text-coral font-bold px-5 py-2.5 rounded-full text-base">🇮🇳 India · Expanding</span>
        </div>
      </Section>

      {/* FAQ preview */}
      <div className="bg-surface py-16">
        <Section className="max-w-3xl">
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl text-center">Frequently asked questions</h2>
          <div className="mt-8 space-y-3">
            {FAQS.map((f) => (
              <details key={f.q} className="bg-white rounded-2xl border border-border p-5 group">
                <summary className="font-bold text-ink cursor-pointer list-none flex justify-between items-center">{f.q}<span className="text-coral group-open:rotate-45 transition-transform text-xl leading-none">+</span></summary>
                <p className="text-ink-600 text-sm mt-3">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="text-center mt-6"><Link to="/faq" className="text-coral font-bold text-sm inline-flex items-center gap-1.5">See all FAQs <ArrowRight size={15} /></Link></div>
        </Section>
      </div>

      {/* Final CTA */}
      <Section className="py-16">
        <div className="bg-ink rounded-3xl p-8 sm:p-12 text-center text-white">
          <h2 className="font-display font-extrabold text-2xl sm:text-4xl">Hungry? Order with BiteGoFood.</h2>
          <p className="text-white/80 mt-3 max-w-lg mx-auto">Food Near You, Faster Than Ever. Create your account and order from local restaurants in minutes.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <Link to="/signup" className="bg-coral text-white font-bold px-6 py-3.5 rounded-full hover:bg-coral-hover transition-colors" data-testid="cta-signup">Create free account</Link>
            <Link to="/contact" className="bg-white/10 text-white font-bold px-6 py-3.5 rounded-full hover:bg-white/20 transition-colors" data-testid="cta-contact">Contact us</Link>
          </div>
        </div>
      </Section>
    </MarketingShell>
  );
}
